import React, { createContext, FC, memo, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import LoadingButton from '@mui/lab/LoadingButton';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import { useBlocker } from 'react-router-dom';

import { CloseIcon, WarningFilledIcon } from 'src/shared/components';
import { useTranslate } from 'src/shared/providers/TranslationProvider';
interface UnsavedChangesProviderProps {
  children: ReactNode;
  onSave?: () => Promise<boolean | undefined>;
  isEdited?: boolean;
  loading?: boolean;
}
interface UnsavedChangesContextType {
  hasUnsavedChanges: boolean;
  onSetHasUnsavedChanges: (value: boolean) => void;
  onSetSaveFunction: (fn: (() => void | Promise<void>) | null) => void;
}
const UnsavedChangesContext = createContext<UnsavedChangesContextType>({} as UnsavedChangesContextType);
export const useUnsavedChanges = (): UnsavedChangesContextType => useContext(UnsavedChangesContext);
export const UnsavedChangesProvider: FC<UnsavedChangesProviderProps> = memo(
  ({ children, onSave: externalOnSave, isEdited: externalIsEdited, loading: externalLoading }) => {
    const { t } = useTranslate();
    const theme = useTheme();
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
    const [saveFunction, setSaveFunction] = useState<(() => void | Promise<void>) | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const blocker = useBlocker(hasUnsavedChanges);
    useEffect(() => {
      if (blocker.state === 'blocked') {
        setIsDialogOpen(true);
      }
    }, [blocker.state]);
    useEffect(() => {
      setHasUnsavedChanges(externalIsEdited || false);
    }, [externalIsEdited]);
    const handleSave = useCallback(async () => {
      setIsSaving(true);
      let isSuccess;
      try {
        if (externalOnSave) {
          isSuccess = await externalOnSave();
        } else if (saveFunction) {
          isSuccess = await saveFunction();
        }
        if (isSuccess) {
          setHasUnsavedChanges(false);
          setIsDialogOpen(false);
          setSaveFunction(null);
          blocker.proceed?.();
        }
      } finally {
        setIsSaving(false);
      }
    }, [blocker, externalOnSave, saveFunction]);
    const handleCancel = useCallback(() => {
      setHasUnsavedChanges(false);
      setIsDialogOpen(false);
      setSaveFunction(null);
      blocker.proceed?.();
    }, [blocker]);
    const handleCloseDialog = useCallback(() => {
      setIsDialogOpen(false);
      blocker.reset?.();
    }, [blocker]);
    useEffect(() => {
      const handleBeforeUnload = (event: BeforeUnloadEvent) => {
        if (hasUnsavedChanges) {
          event.preventDefault();
          event.returnValue = '';
        }
      };
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }, [hasUnsavedChanges]);
    const contextValue: UnsavedChangesContextType = {
      hasUnsavedChanges,
      onSetHasUnsavedChanges: setHasUnsavedChanges,
      onSetSaveFunction: setSaveFunction,
    };
    return (
      <UnsavedChangesContext.Provider value={contextValue}>
        {children}
        <Dialog
          open={isDialogOpen}
          onClose={handleCloseDialog}
          aria-labelledby="unsaved-changes-dialog-title"
          aria-describedby="unsaved-changes-dialog-description"
        >
          <DialogTitle id="unsaved-changes-dialog-title">
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" alignItems="center" spacing={2}>
                <WarningFilledIcon color={theme.palette.warning.main} size={24} />
                <span style={{ marginLeft: '4px' }}>{t('files.unsavedChanges')}</span>
              </Stack>
              <IconButton onClick={handleCloseDialog}>
                <CloseIcon />
              </IconButton>
            </Stack>
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="unsaved-changes-dialog-description">
              {t('files.youHaveUnsavedChangesWouldYouLikeToSave')}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancel} variant="contained" color="secondary">
              {t('global.continue')}
            </Button>
            <LoadingButton
              onClick={handleSave}
              variant="contained"
              color="primary"
              loading={isSaving || externalLoading}
            >
              {t('global.save')}
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </UnsavedChangesContext.Provider>
    );
  },
);
